import Routes from '@common/defs/routes';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import {
  CONTENT_PADDING_X,
  MAX_CONTENT_WIDTH,
  getNavigationItems,
  premiumGradientAnimation,
  premiumIconAnimation,
  TOPBAR_HEIGHT,
  TOPBAR_TOP_MARGIN,
} from '@common/defs/layout';
import Link from 'next/link';
import { Any } from '@common/defs/types';
import useAuth from '@modules/auth/hooks/api/useAuth';

interface TopBarProps {}

const TopBar = (_props: TopBarProps) => {
  const [currentTab, setCurrentTab] = useState<string | false>(false);
  const router = useRouter();
  const currentPath = router.pathname;
  const { user } = useAuth();

  const filteredNavigationItems = getNavigationItems(user).filter((item) => {
    if (item.label === 'Prospecter' && user?.metadata?.prospectingEnabled === false) {
      return false;
    }
    return true;
  });

  const isPremiumTabSelected =
    currentTab !== false &&
    filteredNavigationItems.find((item) => item.href === currentTab)?.premium;

  useEffect(() => {
    const activeItem = filteredNavigationItems.find((item) => item.isActive(currentPath));
    if (activeItem) {
      setCurrentTab(activeItem.href);
    } else {
      setCurrentTab(false);
    }
  }, [currentPath, filteredNavigationItems]);

  return (
    <Box
      sx={{
        position: 'sticky',
        top: TOPBAR_TOP_MARGIN,
        display: 'flex',
        width: '100%',
        justifyContent: 'center',
        alignItems: 'center',
        maxWidth: MAX_CONTENT_WIDTH,
        paddingX: CONTENT_PADDING_X,
        zIndex: 3,
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: TOPBAR_HEIGHT,
          paddingX: 2,
          paddingY: 0,
          gap: 0,
          zIndex: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 6,
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid #e5e7eb',
          backgroundColor: 'common.white',
        }}
      >
        <Box
          component="img"
          src="/images/logos/allo-clients-logo-512.png"
          alt="AlloClients"
          sx={{
            height: { xs: 32, sm: 32 },
            objectFit: 'contain',
            cursor: 'pointer',
          }}
          onClick={() => router.push(Routes.Common.Home)}
        />
        <Tabs
          value={currentTab}
          onChange={(event: Any, newValue: string) => {
            if (
              event.ctrlKey || // Ctrl+Clic
              event.metaKey || // Cmd+Clic (Mac)
              (event.button && event.button === 1) // Clic molette
            ) {
              return;
            }

            setCurrentTab(newValue);
          }}
          sx={{
            width: '100%',
            maxWidth: 600,
            height: '100%',
            '& .MuiTabs-fixed': {
              display: 'flex',
              justifyContent: 'center',
            },
            '& .MuiTabs-list': {
              width: '100%',
            },
            '& .MuiTabs-indicator': isPremiumTabSelected
              ? {
                  background: 'linear-gradient(90deg, #6366f1, #8b5cf6, #ec4899, #6366f1)',
                  backgroundSize: '200% auto',
                  animation: `${premiumGradientAnimation} 4s ease infinite`,
                  height: 3,
                }
              : { backgroundColor: '#0a2540' },
          }}
        >
          {filteredNavigationItems.map((item) => {
            const isSelected = currentTab === item.href;
            const isPremiumActive = item.premium && isSelected;

            return (
              <Tab
                component={Link}
                href={item.href}
                key={item.href}
                label={
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    <item.icon />
                    <Typography variant="body2" fontSize={13} fontWeight={400}>
                      {item.label}
                    </Typography>
                  </Box>
                }
                value={item.href}
                sx={{
                  flex: 1,
                  '&.Mui-selected': { color: '#0a2540' },
                  ...(isPremiumActive
                    ? {
                        '&.Mui-selected': { color: 'transparent' },
                        '& .MuiSvgIcon-root': {
                          animation: `${premiumIconAnimation} 4s ease infinite`,
                        },
                        '& .MuiTypography-root': {
                          background: 'linear-gradient(90deg, #6366f1, #8b5cf6, #ec4899, #6366f1)',
                          backgroundSize: '200% auto',
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          animation: `${premiumGradientAnimation} 4s ease infinite`,
                        },
                      }
                    : {}),
                }}
              />
            );
          })}
        </Tabs>
      </Box>
    </Box>
  );
};

export default TopBar;
